import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import DriverIntakeAdmin from './DriverIntakeAdmin';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { BACKEND_URL as API } from '../../apiConfig';
import { getInternalFleetCopy } from '../../pages/internal/internalFleetI18n';

// 驾照种类列表（日本驾照）
const LICENSE_TYPES = [
  { value: '普通', label: '普通（普通自動車）' },
  { value: '大型二輪', label: '大型二輪' },
  { value: '普通二輪', label: '普通二輪' },
  { value: '小型二輪', label: '小型二輪' },
  { value: '原付', label: '原付' },
  { value: '大型', label: '大型' },
  { value: '中型', label: '中型' },
  { value: '準中型', label: '準中型' },
  { value: '大型二種', label: '大型二種' },
  { value: '中型二種', label: '中型二種' },
  { value: '普通二種', label: '普通二種' },
  { value: '大型特殊', label: '大型特殊' },
  { value: '大型特殊二種', label: '大型特殊二種' },
  { value: '牽引', label: '牽引' },
  { value: '牽引二種', label: '牽引二種' },
];

// 证件照尺寸: 3.0cm x 2.4cm (纵x横), 约 354px x 283px @300dpi
const PHOTO_WIDTH = 283;
const PHOTO_HEIGHT = 354;
const ASPECT_RATIO = PHOTO_WIDTH / PHOTO_HEIGHT;

function addYearsToDateKey(dateKey, years) {
  const match = String(dateKey || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  const [, year, month, day] = match;
  const nextDate = new Date(Date.UTC(Number(year) + Number(years || 0), Number(month) - 1, Number(day)));
  return [
    String(nextDate.getUTCFullYear()).padStart(4, '0'),
    String(nextDate.getUTCMonth() + 1).padStart(2, '0'),
    String(nextDate.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

const FleetDrivers = ({ canEdit = true, language = 'zh' }) => {
  const copy = getInternalFleetCopy(language);
  const t = copy.drivers;
  const common = copy.common;
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showIntake,setShowIntake]=useState(false);
  const [intakeId,setIntakeId]=useState(null);
  const [intakeRefresh,setIntakeRefresh]=useState(0);
  const [saving,setSaving]=useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [hideInactive, setHideInactive] = useState(true); // 默认隐藏已停职
  const [activeDriverFormStep, setActiveDriverFormStep] = useState(0);

  // Photo upload states
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoSrc, setPhotoSrc] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 80, aspect: ASPECT_RATIO });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  const initialForm = {
    name: '',
    name_kana: '',
    birth_date: '',
    address: '',
    phone: '',
    email: '',
    employment_type: 'fulltime',
    photo_url: '',
    license_types: [],
    license_number: '',
    license_expiry: '',
    initial_medical_check: '',
    last_health_check: '',
    next_health_check: '',
    accident_records: '',
    violation_records: '',
    documents_link: '',
    assigned_vehicle_id: '',
    include_in_dispatch_overview: true,
    status: 'active',
    notes: '',
  };

  const [form, setForm] = useState(initialForm);

  const statusOptions = [
    { value: 'active', label: t.active, color: 'bg-emerald-500' },
    { value: 'suspended', label: t.suspended, color: 'bg-red-500' },
  ];

  const employmentTypes = [
    { value: 'fulltime', label: t.fulltime },
    { value: 'parttime', label: t.parttime },
    { value: 'temporary', label: t.temporary },
  ];

  const driverFormSteps = [
    { title: '個人情報', description: '氏名・連絡先・証明写真' },
    { title: '驾驶信息', description: '驾照种类与期限' },
    { title: '健康与合规', description: '体检、事故、违章记录' },
    { title: '关联与文档', description: '车辆、文档、备注' },
  ];

  const driverFormCompletionFields = [
    'name',
    'name_kana',
    'birth_date',
    'address',
    'phone',
    'email',
    'employment_type',
    'photo_url',
    'license_types',
    'license_number',
    'license_expiry',
    'initial_medical_check',
    'last_health_check',
    'next_health_check',
    'accident_records',
    'violation_records',
    'documents_link',
    'assigned_vehicle_id',
    'status',
    'notes',
  ];

  const driverFormCompletedCount = driverFormCompletionFields.filter((field) => {
    const value = form[field];
    if (Array.isArray(value)) return value.length > 0;
    return String(value || '').trim().length > 0;
  }).length;
  const driverFormCompletion = {
    completed: driverFormCompletedCount,
    total: driverFormCompletionFields.length,
    percent: Math.round((driverFormCompletedCount / driverFormCompletionFields.length) * 100),
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [driversRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/api/internal/drivers`),
        axios.get(`${API}/api/internal/vehicles`),
      ]);
      setDrivers(driversRes.data || []);
      setVehicles(vehiclesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if(saving)return;
    if (!form.name.trim() || !form.phone.trim()) {
      alert('请先填写姓名和手机号');
      setActiveDriverFormStep(0);
      return;
    }
    setSaving(true);
    try {
      if(intakeId){await axios.post(`${API}/api/internal/driver-intakes/${intakeId}/accept`,form);setIntakeRefresh(v=>v+1);}
      else if (editingDriver) {
        await axios.put(`${API}/api/internal/drivers/${editingDriver.id}`, form);
      } else {
        await axios.post(`${API}/api/internal/drivers`, form);
      }
      fetchData();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save driver:', error);
      alert('保存失败: ' + (error.response?.data?.detail || error.message));
    } finally {setSaving(false);}
  };

  const handleDelete = async (id) => {
    if (window.confirm('确定要删除这位司机吗？')) {
      try {
        await axios.delete(`${API}/api/internal/drivers/${id}`);
        fetchData();
      } catch (error) {
        console.error('Failed to delete driver:', error);
        alert('删除失败');
      }
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setIntakeId(null);
    setEditingDriver(null);
    setActiveDriverFormStep(0);
  };

  const openEditModal = (driver) => {
    setIntakeId(null);
    setEditingDriver(driver);
    setForm({
      name: driver.name || '',
      name_kana: driver.name_kana || '',
      birth_date: driver.birth_date || '',
      address: driver.address || '',
      phone: driver.phone || '',
      email: driver.email || '',
      employment_type: driver.employment_type || 'fulltime',
      photo_url: driver.photo_url || '',
      license_types: driver.license_types || [],
      license_number: driver.license_number || '',
      license_expiry: driver.license_expiry || '',
      initial_medical_check: driver.initial_medical_check || '',
      last_health_check: driver.last_health_check || '',
      next_health_check: driver.next_health_check || '',
      accident_records: driver.accident_records || '',
      violation_records: driver.violation_records || '',
      documents_link: driver.documents_link || '',
      assigned_vehicle_id: driver.assigned_vehicle_id || '',
      include_in_dispatch_overview: driver.include_in_dispatch_overview !== false,
      status: driver.status || 'active',
      notes: driver.notes || '',
    });
    setActiveDriverFormStep(0);
    setShowModal(true);
  };

  const toggleLicenseType = (type) => {
    const newTypes = form.license_types.includes(type)
      ? form.license_types.filter(t => t !== type)
      : [...form.license_types, type];
    setForm({ ...form, license_types: newTypes });
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const expiry = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  // Photo upload functions
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPhotoSrc(reader.result);
        setShowPhotoModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const cropWidth = Math.min(width * 0.8, height * ASPECT_RATIO * 0.8);
    const cropHeight = cropWidth / ASPECT_RATIO;
    setCrop({
      unit: 'px',
      width: cropWidth,
      height: cropHeight,
      x: (width - cropWidth) / 2,
      y: (height - cropHeight) / 2,
    });
  }, []);

  const getCroppedImg = () => {
    if (!completedCrop || !imgRef.current) return null;
    
    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    
    canvas.width = PHOTO_WIDTH;
    canvas.height = PHOTO_HEIGHT;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      PHOTO_WIDTH,
      PHOTO_HEIGHT
    );
    
    return canvas.toDataURL('image/jpeg', 0.9);
  };

  const handlePhotoUpload = async () => {
    const croppedImage = getCroppedImg();
    if (!croppedImage) {
      alert('请先裁剪照片');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('internal_token');
      const res = await axios.post(`${API}/api/internal/drivers/upload-photo`, {
        image_data: croppedImage,
        driver_id: editingDriver?.id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setForm({ ...form, photo_url: res.data.photo_url });
      setShowPhotoModal(false);
      setPhotoSrc(null);
      alert('照片上传成功');
    } catch (error) {
      console.error('Upload failed:', error);
      alert('上传失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const escapedDriver = driver => Object.fromEntries(Object.entries(driver).map(([k,v])=>[k,typeof v==='string'?v.replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])):v]));
  // Print functions
  const printSingleDriver = (driver) => {
    driver=escapedDriver(driver);
    const printWindow = window.open('', '_blank');
    const photoUrl = driver.photo_url ? `${API}${driver.photo_url}` : '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>司机信息 - ${driver.name}</title>
        <style>
          body { font-family: 'Hiragino Sans', 'Microsoft YaHei', sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; }
          .header h1 { margin: 0; font-size: 24px; }
          .header p { margin: 5px 0 0; color: #666; }
          .driver-card { display: flex; gap: 30px; margin-bottom: 30px; }
          .photo-section { flex-shrink: 0; }
          .photo { width: 90px; height: 113px; border: 1px solid #ccc; display: flex; align-items: center; justify-content: center; background: #f5f5f5; }
          .photo img { width: 100%; height: 100%; object-fit: cover; }
          .info-section { flex: 1; }
          .section { margin-bottom: 20px; }
          .section-title { font-weight: bold; font-size: 14px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-bottom: 10px; }
          .field { display: flex; margin-bottom: 8px; }
          .field-label { width: 120px; color: #666; font-size: 12px; }
          .field-value { flex: 1; font-size: 12px; }
          .license-tags { display: flex; flex-wrap: wrap; gap: 5px; }
          .license-tag { background: #e0e7ff; color: #3730a3; padding: 2px 8px; border-radius: 4px; font-size: 11px; }
          .footer { text-align: center; margin-top: 30px; font-size: 11px; color: #999; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>湘南国際株式会社</h1>
          <p>乗務員台帳 / 司机信息表</p>
        </div>
        
        <div class="driver-card">
          <div class="photo-section">
            <div class="photo">
              ${photoUrl ? `<img src="${photoUrl}" alt="证件照"/>` : '无照片'}
            </div>
          </div>
          <div class="info-section">
            <div class="section">
              <div class="section-title">基本信息</div>
              <div class="field"><span class="field-label">姓名:</span><span class="field-value">${driver.name || '-'}</span></div>
              <div class="field"><span class="field-label">假名:</span><span class="field-value">${driver.name_kana || '-'}</span></div>
              <div class="field"><span class="field-label">出生日期:</span><span class="field-value">${driver.birth_date || '-'}</span></div>
              <div class="field"><span class="field-label">住址:</span><span class="field-value">${driver.address || '-'}</span></div>
              <div class="field"><span class="field-label">电话:</span><span class="field-value">${driver.phone || '-'}</span></div>
              <div class="field"><span class="field-label">邮箱:</span><span class="field-value">${driver.email || '-'}</span></div>
              <div class="field"><span class="field-label">雇佣类型:</span><span class="field-value">${driver.employment_type === 'fulltime' ? '全职' : driver.employment_type === 'parttime' ? '兼职' : '短期'}</span></div>
              <div class="field"><span class="field-label">状态:</span><span class="field-value">${driver.status === 'active' ? '在职' : '停职'}</span></div>
            </div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">驾驶信息</div>
          <div class="field">
            <span class="field-label">驾照种类:</span>
            <span class="field-value">
              <div class="license-tags">
                ${(driver.license_types || []).map(t => `<span class="license-tag">${t}</span>`).join('') || '-'}
              </div>
            </span>
          </div>
          <div class="field"><span class="field-label">驾照号码:</span><span class="field-value">${driver.license_number || '-'}</span></div>
          <div class="field"><span class="field-label">驾照到期日:</span><span class="field-value">${driver.license_expiry || '-'}</span></div>
        </div>
        
        <div class="section">
          <div class="section-title">健康与合规</div>
          <div class="field"><span class="field-label">初任诊断日期:</span><span class="field-value">${driver.initial_medical_check || '-'}</span></div>
          <div class="field"><span class="field-label">最近健康诊断:</span><span class="field-value">${driver.last_health_check || '-'}</span></div>
          <div class="field"><span class="field-label">下次诊断到期:</span><span class="field-value">${driver.next_health_check || '-'}</span></div>
          <div class="field"><span class="field-label">事故记录:</span><span class="field-value">${driver.accident_records || '无'}</span></div>
          <div class="field"><span class="field-label">违章记录:</span><span class="field-value">${driver.violation_records || '无'}</span></div>
        </div>
        
        <div class="section">
          <div class="section-title">关联信息</div>
          <div class="field"><span class="field-label">常用车辆:</span><span class="field-value">${driver.assigned_vehicle_plate || '-'}</span></div>
          <div class="field"><span class="field-label">文档链接:</span><span class="field-value">${driver.documents_link || '-'}</span></div>
          <div class="field"><span class="field-label">备注:</span><span class="field-value">${driver.notes || '-'}</span></div>
        </div>
        
        <div class="footer">
          打印日期: ${new Date().toLocaleDateString('zh-CN')} | 湘南国際株式会社 内部管理系统
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const printSelectedDrivers = () => {
    if (selectedDrivers.length === 0) {
      alert('请先选择要打印的司机');
      return;
    }
    
    const selectedData = drivers.filter(d => selectedDrivers.includes(d.id));
    const printWindow = window.open('', '_blank');
    
    const driversHtml = selectedData.map(driver => {
      driver=escapedDriver(driver);
      const photoUrl = driver.photo_url ? `${API}${driver.photo_url}` : '';
      return `
        <div class="driver-page">
          <div class="driver-card">
            <div class="photo-section">
              <div class="photo">
                ${photoUrl ? `<img src="${photoUrl}" alt="证件照"/>` : '无照片'}
              </div>
            </div>
            <div class="basic-info">
              <h2>${driver.name} ${driver.name_kana ? `(${driver.name_kana})` : ''}</h2>
              <p>电话: ${driver.phone || '-'} | 邮箱: ${driver.email || '-'}</p>
              <p>雇佣: ${driver.employment_type === 'fulltime' ? '全职' : driver.employment_type === 'parttime' ? '兼职' : '短期'} | 状态: ${driver.status === 'active' ? '在职' : '停职'}</p>
            </div>
          </div>
          <table class="info-table">
            <tr><td class="label">住址</td><td>${driver.address || '-'}</td><td class="label">出生日期</td><td>${driver.birth_date || '-'}</td></tr>
            <tr><td class="label">驾照种类</td><td colspan="3">${(driver.license_types || []).join(', ') || '-'}</td></tr>
            <tr><td class="label">驾照号码</td><td>${driver.license_number || '-'}</td><td class="label">驾照到期</td><td>${driver.license_expiry || '-'}</td></tr>
            <tr><td class="label">初任诊断</td><td>${driver.initial_medical_check || '-'}</td><td class="label">下次诊断</td><td>${driver.next_health_check || '-'}</td></tr>
            <tr><td class="label">事故记录</td><td>${driver.accident_records || '无'}</td><td class="label">违章记录</td><td>${driver.violation_records || '无'}</td></tr>
            <tr><td class="label">常用车辆</td><td>${driver.assigned_vehicle_plate || '-'}</td><td class="label">备注</td><td>${driver.notes || '-'}</td></tr>
          </table>
        </div>
      `;
    }).join('<div class="page-break"></div>');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>司机信息批量打印</title>
        <style>
          body { font-family: 'Hiragino Sans', 'Microsoft YaHei', sans-serif; padding: 20px; }
          .header { text-align: center; margin-bottom: 20px; }
          .header h1 { margin: 0; font-size: 20px; }
          .driver-page { margin-bottom: 30px; padding-bottom: 20px; border-bottom: 1px dashed #ccc; }
          .driver-card { display: flex; gap: 20px; margin-bottom: 15px; }
          .photo { width: 72px; height: 90px; border: 1px solid #ccc; background: #f5f5f5; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #999; }
          .photo img { width: 100%; height: 100%; object-fit: cover; }
          .basic-info h2 { margin: 0 0 5px; font-size: 16px; }
          .basic-info p { margin: 3px 0; font-size: 11px; color: #666; }
          .info-table { width: 100%; border-collapse: collapse; font-size: 11px; }
          .info-table td { border: 1px solid #ddd; padding: 6px 8px; }
          .info-table .label { background: #f5f5f5; width: 80px; font-weight: bold; }
          .page-break { page-break-after: always; }
          .footer { text-align: center; font-size: 10px; color: #999; margin-top: 20px; }
          @media print { .page-break { page-break-after: always; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>湘南国際株式会社 - 乗務員台帳</h1>
        </div>
        ${driversHtml}
        <div class="footer">
          打印日期: ${new Date().toLocaleDateString('zh-CN')} | 共 ${selectedData.length} 位司机
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const toggleSelectDriver = (id) => {
    setSelectedDrivers(prev => 
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDrivers.length === filteredDrivers.length) {
      setSelectedDrivers([]);
    } else {
      setSelectedDrivers(filteredDrivers.map(d => d.id));
    }
  };

  const filteredDrivers = drivers.filter(d => {
    const matchesSearch = d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          d.phone?.includes(searchTerm) ||
                          d.license_number?.includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || d.status === filterStatus;
    const matchesType = filterType === 'all' || d.employment_type === filterType;
    // 一键隐藏已停职
    const matchesHideInactive = !hideInactive || d.status !== 'suspended';
    return matchesSearch && matchesStatus && matchesType && matchesHideInactive;
  });

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#d8b76a] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="internal-page-skin space-y-6" data-testid="fleet-drivers-black-gold-page">
      {/* Header */}
      <div className="internal-luxury-card flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <input
            type="text"
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="payroll-field min-w-[240px]"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="payroll-field w-auto min-w-[150px]"
          >
            <option value="all">{common.allStatuses}</option>
            {statusOptions.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="payroll-field w-auto min-w-[150px]"
          >
            <option value="all">{common.allTypes}</option>
            {employmentTypes.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          {/* 一键隐藏已停职开关 */}
          <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-amber-300/20 bg-black/30 px-4 py-2 transition-colors hover:border-amber-300/35 hover:bg-amber-300/10">
            <input
              type="checkbox"
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              className="h-4 w-4 rounded border-amber-300/30 bg-black/40 text-[#d8b76a] focus:ring-amber-300/30"
            />
            <span className="text-white text-sm whitespace-nowrap">{t.hideInactive}</span>
          </label>
        </div>
        <div className="flex gap-3">
          {selectedDrivers.length > 0 && (
            <button
              onClick={printSelectedDrivers}
              className="internal-graphite-button !py-2"
            >
              🖨️ {t.printSelected} ({selectedDrivers.length})
            </button>
          )}
          {canEdit&&<button className="internal-graphite-button !py-2" onClick={()=>setShowIntake(v=>!v)}>{language==='ja'?'入力リンク・提出資料':'填写链接／提交资料'}</button>}
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="internal-gold-button !py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="add-driver-btn"
            disabled={!canEdit}
            title={!canEdit ? common.noEditPermission : ''}
          >
            + {t.addDriver}
          </button>
        </div>
      </div>

      {showIntake&&canEdit&&<DriverIntakeAdmin key={intakeRefresh} language={language} onReview={row=>{resetForm();setIntakeId(row.id);setForm({...initialForm,...row.profile});setShowModal(true);}}/>}
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <div className="internal-luxury-panel p-4">
          <p className="text-slate-400 text-sm">{t.total}</p>
          <p className="text-white text-2xl font-bold">{drivers.length}</p>
        </div>
        <div className="internal-luxury-panel p-4">
          <p className="text-slate-400 text-sm">{t.active}</p>
          <p className="text-emerald-400 text-2xl font-bold">{drivers.filter(d => d.status === 'active').length}</p>
        </div>
        <div className="internal-luxury-panel p-4">
          <p className="text-slate-400 text-sm">{t.fulltime}</p>
          <p className="text-[#f3d98b] text-2xl font-bold">{drivers.filter(d => d.employment_type === 'fulltime').length}</p>
        </div>
        <div className="internal-luxury-panel p-4">
          <p className="text-slate-400 text-sm">{t.parttimeTemporary}</p>
          <p className="text-[#d8b76a] text-2xl font-bold">{drivers.filter(d => d.employment_type !== 'fulltime').length}</p>
        </div>
        <div className="internal-luxury-panel p-4">
          <p className="text-slate-400 text-sm">{t.expiring}</p>
          <p className="text-red-400 text-2xl font-bold">
            {drivers.filter(d => isExpiringSoon(d.license_expiry) || isExpiringSoon(d.next_health_check)).length}
          </p>
        </div>
      </div>

      {/* Driver List */}
      <div className="internal-luxury-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black/30">
              <tr>
                <th className="text-left text-slate-400 text-sm font-medium px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selectedDrivers.length === filteredDrivers.length && filteredDrivers.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded bg-slate-600 border-slate-500"
                  />
                </th>
                <th className="text-left text-slate-400 text-sm font-medium px-4 py-4">{t.photo}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-4 py-4">{t.name}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-4 py-4">{t.contact}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-4 py-4">{t.employmentType}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-4 py-4">{t.licenseExpiry}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-4 py-4">{t.healthExpiry}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-4 py-4">{t.usualVehicle}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-4 py-4 whitespace-nowrap">{t.dispatchOverviewTarget}</th>
                <th className="text-left text-slate-400 text-sm font-medium px-4 py-4 whitespace-nowrap">{common.status}</th>
                <th className="text-center text-slate-400 text-sm font-medium px-4 py-4">{common.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-300/10">
              {filteredDrivers.map((driver) => (
                <tr key={driver.id} className="transition-colors hover:bg-amber-300/[0.045]">
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedDrivers.includes(driver.id)}
                      onChange={() => toggleSelectDriver(driver.id)}
                      className="w-4 h-4 rounded bg-slate-600 border-slate-500"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="h-12 w-10 overflow-hidden rounded border border-amber-300/20 bg-black/30">
                      {driver.photo_url ? (
                        <img src={`${API}${driver.photo_url}`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs">{common.none}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div>
                      <span className="text-white font-medium">{driver.name}</span>
                      {driver.name_kana && (
                        <p className="text-slate-500 text-sm">{driver.name_kana}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div>
                      <span className="text-slate-300">{driver.phone}</span>
                      {driver.email && (
                        <p className="text-slate-500 text-sm">{driver.email}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`internal-status-badge internal-driver-employment-badge ${
                      driver.employment_type === 'fulltime' ? 'border-amber-300/25 bg-amber-300/10 text-[#f3d98b]' :
                      driver.employment_type === 'parttime' ? 'border-white/10 bg-white/5 text-slate-300' :
                      'border-amber-400/25 bg-amber-500/20 text-amber-400'
                    }`}>
                      {employmentTypes.find(t => t.value === driver.employment_type)?.label}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`${
                      isExpired(driver.license_expiry) ? 'text-red-400 font-bold' :
                      isExpiringSoon(driver.license_expiry) ? 'text-amber-400' :
                      'text-slate-300'
                    }`}>
                      {driver.license_expiry || '-'}
                      {isExpired(driver.license_expiry) && <span className="ml-1 text-xs">({common.expired})</span>}
                      {isExpiringSoon(driver.license_expiry) && <span className="ml-1 text-xs">({common.expiringSoon})</span>}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`${
                      isExpired(driver.next_health_check) ? 'text-red-400 font-bold' :
                      isExpiringSoon(driver.next_health_check) ? 'text-amber-400' :
                      'text-slate-300'
                    }`}>
                      {driver.next_health_check || '-'}
                      {isExpired(driver.next_health_check) && <span className="ml-1 text-xs">({common.expired})</span>}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-slate-300">{driver.assigned_vehicle_plate || '-'}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`internal-status-badge ${
                      driver.include_in_dispatch_overview === false ? 'bg-white/5 text-slate-400' : 'bg-amber-300/15 text-[#f3d98b]'
                    }`}>
                      {driver.include_in_dispatch_overview === false ? t.dispatchHidden : t.dispatchVisible}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`internal-status-badge ${
                      driver.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {statusOptions.find(s => s.value === driver.status)?.label}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() => printSingleDriver(driver)}
                      className="mr-2 text-[#d8b76a] hover:text-[#f3d98b]"
                      title="打印"
                    >
                      🖨️
                    </button>
                    {driver.documents_link && (
                      <a
                        href={driver.documents_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mr-2 text-[#d8b76a] hover:text-[#f3d98b]"
                        title="查看文档"
                      >
                        📄
                      </a>
                    )}
                    {canEdit && (
                      <>
                        <button
                          onClick={() => openEditModal(driver)}
                          className="mr-2 font-semibold text-[#d8b76a] hover:text-[#f3d98b]"
                        >
                          {common.edit}
                        </button>
                        <button
                          onClick={() => handleDelete(driver.id)}
                          className="font-semibold text-red-300 hover:text-red-200"
                        >
                          {common.delete}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredDrivers.length === 0 && (
          <div className="py-12 text-center text-slate-500">
            {t.empty}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-md">
          <div className="internal-luxury-card max-h-[90vh] w-full max-w-3xl overflow-y-auto p-6">
            {intakeId&&<p className="text-amber-200 mb-3">{language==='ja'?'提出情報を確認して登録':'核对司机自行提交的资料后保存'} · {intakeId}</p>}
            <h3 className="text-white text-xl font-bold mb-6">
              {editingDriver ? t.editTitle : t.addTitle}
            </h3>
            <div className="mb-6 rounded-xl border border-amber-300/15 bg-black/25 p-4" data-testid="driver-form-progress">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#f3d98b]">
                    {driverFormSteps[activeDriverFormStep].title}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    第 {activeDriverFormStep + 1} / {driverFormSteps.length} 步 · {driverFormSteps[activeDriverFormStep].description}
                  </p>
                </div>
                <div className="text-sm font-semibold text-slate-300">
                  已填写 {driverFormCompletion.completed} / {driverFormCompletion.total}（{driverFormCompletion.percent}%）
                </div>
              </div>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-[#d8b76a] transition-all"
                  style={{ width: `${driverFormCompletion.percent}%` }}
                  aria-label={`已填写 ${driverFormCompletion.percent}%`}
                />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
                {driverFormSteps.map((step, index) => (
                  <button
                    key={step.title}
                    type="button"
                    onClick={() => setActiveDriverFormStep(index)}
                    className={`rounded-lg border px-3 py-2 text-left transition ${
                      index === activeDriverFormStep
                        ? 'border-amber-300/50 bg-amber-300/10 text-[#f3d98b]'
                        : 'border-white/10 bg-black/20 text-slate-400 hover:border-amber-300/25 hover:text-slate-200'
                    }`}
                  >
                    <span className="block text-xs font-semibold">Step {index + 1}</span>
                    <span className="mt-1 block text-sm font-semibold">{step.title}</span>
                  </button>
                ))}
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* 基本信息 + 照片 */}
              {activeDriverFormStep === 0 && (
              <div>
                <h4 className="mb-3 border-b border-amber-300/20 pb-2 font-medium text-[#f3d98b]">基本信息</h4>
                <div className="flex gap-6">
                  {/* 照片区域 */}
                  <div className="flex-shrink-0">
                    <label className="mb-2 block text-sm font-semibold text-[#f3d98b]">司机头像（选填）</label>
                    <div 
                      className="flex h-[113px] w-[90px] cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-amber-300/25 bg-black/30 transition-colors hover:border-amber-300/50"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {form.photo_url ? (
                        <img src={form.photo_url.startsWith('data:')?form.photo_url:`${API}${form.photo_url}`} alt="证件照" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center text-slate-500 text-xs p-2">
                          <div className="text-2xl mb-1">📷</div>
                          点击上传
                        </div>
                      )}
                    </div>
                    <p
                      className="mt-2 max-w-[160px] text-xs leading-relaxed text-amber-100/80"
                      data-testid="driver-photo-optional-hint"
                    >
                      管理员可不上传照片直接保存。如需上传，请使用正装照片。上传后请自己调整裁剪框，尺寸按证件照 3.0×2.4cm 裁剪。
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                  
                  {/* 基本字段 */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                    <label className="mb-1 block text-sm text-slate-400">姓名 *</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="payroll-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-slate-400">假名</label>
                      <input
                        type="text"
                        value={form.name_kana}
                        onChange={(e) => setForm({ ...form, name_kana: e.target.value })}
                        className="payroll-field"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-slate-400">出生日期</label>
                      <input
                        type="date"
                        value={form.birth_date}
                        onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                        className="payroll-field"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-slate-400">手机号 *</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="payroll-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-slate-400">邮箱</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="payroll-field"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-slate-400">雇佣类型</label>
                      <select
                        value={form.employment_type}
                        onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
                        className="payroll-field"
                      >
                        {employmentTypes.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="mb-1 block text-sm text-slate-400">住址</label>
                  <input
                    type="text"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="payroll-field"
                  />
                </div>
              </div>
              )}

              {/* 驾驶信息 */}
              {activeDriverFormStep === 1 && (
              <div>
                <h4 className="mb-3 border-b border-amber-300/20 pb-2 font-medium text-[#f3d98b]">驾驶信息</h4>
                <div className="space-y-4">
                  {/* 驾照种类 - 矩阵多选 */}
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">驾照种类（可多选）</label>
                    <div className="grid grid-cols-3 gap-2 rounded-xl border border-amber-300/15 bg-black/25 p-4 md:grid-cols-5">
                      {LICENSE_TYPES.map(lt => (
                        <label key={lt.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.license_types.includes(lt.value)}
                            onChange={() => toggleLicenseType(lt.value)}
                            className="w-4 h-4 rounded bg-slate-600 border-slate-500"
                          />
                          <span className="text-slate-300 text-sm">{lt.label}</span>
                        </label>
                      ))}
                    </div>
                    {form.license_types.length > 0 && (
                      <p className="mt-2 text-sm text-[#f3d98b]">
                        已选: {form.license_types.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">驾照号码</label>
                      <input
                        type="text"
                        value={form.license_number}
                        onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                        className="payroll-field"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-1">驾照到期日</label>
                      <input
                        type="date"
                        value={form.license_expiry}
                        onChange={(e) => setForm({ ...form, license_expiry: e.target.value })}
                        className="payroll-field"
                      />
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* 健康与合规 */}
              {activeDriverFormStep === 2 && (
              <div>
                <h4 className="mb-3 border-b border-amber-300/20 pb-2 font-medium text-[#f3d98b]">健康与合规</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">初任诊断日期</label>
                    <input
                      type="date"
                      value={form.initial_medical_check}
                      onChange={(e) => setForm({ ...form, initial_medical_check: e.target.value })}
                      className="payroll-field"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">最近健康诊断日期</label>
                    <input
                      type="date"
                      value={form.last_health_check}
                      onChange={(e) => {
                        const lastDate = e.target.value;
                        const nextDate = lastDate ? addYearsToDateKey(lastDate, 1) : '';
                        setForm({ ...form, last_health_check: lastDate, next_health_check: nextDate });
                      }}
                      className="payroll-field"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">体检到期日 <span className="text-slate-500">(自动计算，有效期一年)</span></label>
                    <input
                      type="date"
                      value={form.next_health_check}
                      readOnly
                      className="payroll-field cursor-not-allowed !bg-white/5 !text-slate-400"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">事故记录</label>
                    <textarea
                      value={form.accident_records}
                      onChange={(e) => setForm({ ...form, accident_records: e.target.value })}
                      className="payroll-field"
                      rows={2}
                      placeholder="无事故记录"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">违章记录</label>
                    <textarea
                      value={form.violation_records}
                      onChange={(e) => setForm({ ...form, violation_records: e.target.value })}
                      className="payroll-field"
                      rows={2}
                      placeholder="无违章记录"
                    />
                  </div>
                </div>
              </div>
              )}

              {/* 关联与文档 */}
              {activeDriverFormStep === 3 && (
              <div>
                <h4 className="mb-3 border-b border-amber-300/20 pb-2 font-medium text-[#f3d98b]">关联与文档</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">常用车辆</label>
                    <select
                      value={form.assigned_vehicle_id}
                      onChange={(e) => setForm({ ...form, assigned_vehicle_id: e.target.value })}
                      className="payroll-field"
                    >
                      <option value="">-- 未分配 --</option>
                      {vehicles.filter(v => v.status === 'active').map(v => (
                        <option key={v.id} value={v.id}>{v.plate_number}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">Documents Link (Box URL)</label>
                    <input
                      type="url"
                      value={form.documents_link}
                      onChange={(e) => setForm({ ...form, documents_link: e.target.value })}
                      className="payroll-field"
                      placeholder="https://box.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">状态</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="payroll-field"
                    >
                      {statusOptions.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">{t.dispatchOverviewTarget}</label>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, include_in_dispatch_overview: !form.include_in_dispatch_overview })}
                      className={`flex min-h-[44px] w-full items-center justify-between rounded-xl border px-4 py-2 text-left transition ${
                        form.include_in_dispatch_overview
                          ? 'border-amber-300/35 bg-amber-300/10 text-[#f3d98b]'
                          : 'border-white/10 bg-black/30 text-slate-400'
                      }`}
                      aria-pressed={form.include_in_dispatch_overview}
                    >
                      <span>
                        <span className="block text-sm font-semibold">
                          {form.include_in_dispatch_overview ? t.dispatchVisible : t.dispatchHidden}
                        </span>
                        <span className="mt-0.5 block text-xs text-slate-500">{t.dispatchOverviewHint}</span>
                      </span>
                      <span className={`relative h-6 w-11 rounded-full transition ${
                        form.include_in_dispatch_overview ? 'bg-[#d8b76a]' : 'bg-slate-700'
                      }`}>
                        <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition ${
                          form.include_in_dispatch_overview ? 'left-6' : 'left-1'
                        }`} />
                      </span>
                    </button>
                  </div>
                </div>

                {/* 备注 */}
                <div className="mt-4">
                  <label className="block text-slate-400 text-sm mb-1">备注</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="payroll-field"
                    rows={3}
                  />
                </div>
              </div>
              )}

              <div className="flex justify-end gap-3 border-t border-amber-300/20 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="internal-graphite-button !py-2"
                >
                  {common.cancel}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveDriverFormStep(Math.max(activeDriverFormStep - 1, 0))}
                  className="internal-graphite-button !py-2 disabled:opacity-50"
                  disabled={activeDriverFormStep === 0}
                >
                  上一步
                </button>
                {activeDriverFormStep < driverFormSteps.length - 1 && (
                  <button
                    type="button"
                    onClick={() => setActiveDriverFormStep(Math.min(activeDriverFormStep + 1, driverFormSteps.length - 1))}
                    className="internal-gold-button !py-2"
                  >
                    下一步
                  </button>
                )}
                {activeDriverFormStep === driverFormSteps.length - 1 && (
                <button
                  type="submit"
                  className="internal-gold-button !py-2"
                >
                  {common.save}
                </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Photo Crop Modal */}
      {showPhotoModal && photoSrc && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md">
          <div className="internal-luxury-card w-full max-w-lg p-6">
            <h3 className="text-white text-xl font-bold mb-4">裁剪证件照</h3>
            <p className="text-slate-400 text-sm mb-4">
              上传后请自己调整裁剪框，把头像裁成正装证件照尺寸 (3.0cm × 2.4cm)，再确认上传。
            </p>
            
            <div className="mb-4 flex justify-center rounded-xl border border-amber-300/15 bg-black/35 p-4">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={ASPECT_RATIO}
                minWidth={50}
              >
                <img
                  src={photoSrc}
                  onLoad={onImageLoad}
                  alt="Upload"
                  style={{ maxHeight: '400px' }}
                />
              </ReactCrop>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowPhotoModal(false); setPhotoSrc(null); }}
                className="internal-graphite-button !py-2"
              >
                取消
              </button>
              <button
                onClick={handlePhotoUpload}
                disabled={uploading}
                className="internal-gold-button !py-2 disabled:opacity-50"
              >
                {uploading ? '上传中...' : '确认裁剪后上传'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetDrivers;
